import { provideApolloClient } from "@vue/apollo-composable";

import { useCurrentApolloClient } from "@/plugins/apollo";
import { CHATBOT_CHAT, CHATBOT_ENTITIES, CHATBOT_FEEDBACK, CHATBOT_RESET_THREAD } from "./queries";
import { mapAssets, mapEntities, ChatbotEntity } from "./models";

provideApolloClient(useCurrentApolloClient());

async function sendChatMessage(message: string, threadId?: string) {
  const result = await useCurrentApolloClient().mutate({
    mutation: CHATBOT_CHAT,
    variables: { message, threadId },
  });

  const data = result.data.chatbot_chat;

  return {
    response: data.response as string,
    runId: data.runId as string,
    assets: mapAssets(data.assets),
    // Machine-readable budget / rate-limit code carried as data (survives Hasura
    // remote-schema error rewriting); the store turns it into a localized bubble.
    errorCode: (data.errorCode as string | null) ?? undefined,
  };
}

async function submitChatbotFeedback(runId: string, score?: number, comment?: string) {
  const result = await useCurrentApolloClient().mutate({
    mutation: CHATBOT_FEEDBACK,
    variables: { runId, score, comment },
  });

  return !!result.data.chatbot_feedback;
}

async function fetchChatbotEntities(): Promise<ChatbotEntity[]> {
  const result = await useCurrentApolloClient().query({
    query: CHATBOT_ENTITIES,
    fetchPolicy: "no-cache",
  });

  return mapEntities(result.data.chatbot_entities);
}

async function resetChatbotThread(): Promise<string | undefined> {
  const result = await useCurrentApolloClient().query({
    query: CHATBOT_RESET_THREAD,
    fetchPolicy: "no-cache",
  });

  return result.data.chatbot_reset_thread?.threadId;
}

export function useChatbotService() {
  return {
    sendChatMessage,
    submitChatbotFeedback,
    fetchChatbotEntities,
    resetChatbotThread,
  };
}
